/**
 * Fase 2B · B4 · TrialExpiredBanner
 * Banner soft-lock post-trial. NO destructivo: los datos siguen visibles,
 * pero se invita explícitamente a elegir un plan para reactivar operaciones.
 *
 * Consume /api/billing/status. Se muestra cuando:
 *   subscription_status === "trial_expired"
 *   o `needs_payment === true` y NO está en estado "trialing".
 *
 * NO reemplaza TrialWidget · convive con él (TrialWidget solo pinta trialing).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WarningCircle, Lock, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function TrialExpiredBanner() {
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    let mounted = true;
    let cancelled = false;
    const fetchStatus = async (attempt = 0) => {
      try {
        const { data } = await api.get("/billing/status");
        if (mounted) setBilling(data);
      } catch (e) {
        // Rate-limits (429) o errores transitorios · reintenta hasta 2 veces con backoff
        if (!cancelled && attempt < 2) {
          const wait = 3000 * (attempt + 1);
          setTimeout(() => { if (!cancelled) fetchStatus(attempt + 1); }, wait);
        }
      }
    };
    fetchStatus();
    return () => { mounted = false; cancelled = true; };
  }, []);

  if (!billing) return null;

  const status = billing.subscription_status;
  const shouldShow = status === "trial_expired" ||
                     status === "past_due" ||
                     (billing.needs_payment && status !== "trialing" && status !== "active" && status !== "lifetime");

  if (!shouldShow) return null;

  const title = status === "past_due"
    ? "Hay un problema con tu pago"
    : "Tu prueba ha terminado";
  const desc = status === "past_due"
    ? "No hemos podido cobrar tu suscripción. Actualiza tu método de pago para restaurar todas las funciones."
    : "Tus datos siguen seguros. Elige un plan para volver a crear citas, facturas y aprovechar el marketplace destacado.";

  return (
    <section
      className="ag-card p-5 flex items-start gap-4 flex-wrap border border-red-500/40 bg-red-500/10"
      data-testid="trial-expired-banner"
      role="alert"
      aria-live="polite"
    >
      <div className="w-12 h-12 grid place-items-center rounded-full bg-red-500/15 border border-red-500/30 shrink-0">
        <Lock size={22} weight="duotone" className="text-red-300" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-red-300 font-semibold flex items-center gap-1.5">
          <WarningCircle size={11} weight="fill" /> Acceso limitado
        </div>
        <div className="font-display font-bold text-xl md:text-2xl tracking-tighter mt-1 text-red-100">
          {title}
        </div>
        <p className="text-xs text-red-200/80 mt-1.5 leading-relaxed">
          {desc}
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
          <ShieldCheck size={13} weight="fill" />
          Tus organizaciones, clientes, facturas y datos no se han eliminado
        </div>
      </div>
      <Link
        to="/precios"
        className="ag-btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider shrink-0"
        data-testid="trial-expired-cta"
      >
        Elegir plan <ArrowRight size={14} weight="bold" />
      </Link>
    </section>
  );
}
